import {Component, ElementRef, inject, signal, ViewChild} from '@angular/core';
import {Html5Qrcode} from "html5-qrcode";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute, Router} from "@angular/router";
import {CustomMessageService, LocalStorageService, ResponseHttpError, UtilService} from "library";
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {PathNavigationService} from 'projects/store/src/app/services/api/v1/path-navigation.service';
import {forkJoin} from "rxjs";
import {OriginMessageError} from "library";
import {SalesWorkflow, SalesWorkflowSteps, RequestStatus, AgreementStatus, LocalStorageKey} from "projects/store/src/app/enums";
import {firstValueFrom} from 'rxjs';
import {WorkflowStep} from "projects/store/src/app/interfaces";
import {environment} from 'projects/store/src/environments/environment';
import {MacroFlowService} from 'projects/store/src/app/services/api/v1/macro-flow.service';
import {AuthService} from 'projects/store/src/app/services/api/v1/auth.service';

enum OptionsSelect {
  'codigoQR' = 'codigoQR',
  'documentoIdentidad' = 'documentoIdentidad',
  'listarSolicitudes' = 'listarSolicitudes'
}
const ATTRIBUTES_CODE = 'solicitud_autorizacion_onp_info';

@Component({
  selector: 'app-sales-self-management',
  templateUrl: './sales-self-management.component.html',
  styles: [
  ]
})
export class SalesSelfManagementComponent {
  routesBreadcrumbs = ['VENTAS', 'Autogestión', 'Retomar'];
  scanner: Html5Qrcode | null = null;
  form: FormGroup;
  option = OptionsSelect;
  selectedOption: any;
  visibleQRModal = false;
  visibleDocumentModal = false;
  requestID!: string;
  categoryCode!: string;
  isScanning = signal(false);
  isLoading = signal(false);
  isSubmitted = signal(false);
  hasError = signal(false);
  errorMessage = '';
  textErrorMessage = 'No existe una solicitud para este documento';
  textErrorMessageInProgress = 'La solicitud está en progreso por parte del cliente.';
  currentCategoryStepId!: string;
  pathNext: undefined | string = undefined;
  flowCode: string;
  requests: any[] = [];
  storeRequests: any[] = [];
  homeRequests: any[] = [];
  storeRequestsCount: number = 0;
  homeRequestsCount: number = 0;
  salesSiteURL = environment.salesSiteURL;
  stepRequest: boolean = false;


  private readonly _requestService = inject(RequestService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _customMessageService = inject(CustomMessageService);
  private readonly _salesWorkflowService = inject(SalesWorkflowService);
  private readonly _utilService = inject(UtilService);
  private readonly _macroFlowService = inject(MacroFlowService);
  private readonly _authService = inject(AuthService);
  private readonly _localStorageService = inject(LocalStorageService);

  protected readonly AgreementStatus = AgreementStatus;

  @ViewChild('reader', { static: false }) reader!: ElementRef;

  constructor(private pathNavigationService: PathNavigationService) {
    this.form = new FormGroup({
      document: new FormControl(null, [Validators.required]),
    });
    this.flowCode = this._route.snapshot.params['flow_code'];
  }

  async startScanner() {
    try {
      const cameras = await Html5Qrcode.getCameras();

      if (cameras.length === 0) {
        alert("No se encontraron cámaras disponibles.");
        return;
      }

      const backCamera = cameras.find(cam =>
        cam.label.toLowerCase().includes('back') || cam.label.toLowerCase().includes('trasera')
      );

      // Usara la cámara trasera si existe, si no la primera
      const cameraId = backCamera ? backCamera.id : cameras[0].id;
      this.scanner = new Html5Qrcode("reader");

      await this.scanner.start(
        cameraId,
        { fps: 10, qrbox: { width: 231, height: 231 } },
        (requestId: string) => {
          console.log(`Código QR detectado: ${requestId}`);
          this.requestID = requestId;
          this.stopScanner();
          this.getRequest(requestId, "scanner");
        },
        (errorMessage: string) => {
          console.warn(`Error escaneando: ${errorMessage}`);
        }
      );

      this.isScanning.set(true);
    } catch (error) {
      console.error("Error al acceder a la cámara:", error);
      alert("Debes permitir el acceso a la cámara.");
    }
  }


  stopScanner() {
    if (this.scanner) {
      this.scanner.stop().then(() => {
        this.scanner = null;
        this.isScanning.set(false);
      }).catch((err) => console.error("Error al detener el escáner", err));
    }
  }

  onStartWitchDocument(){
    this.isScanning.set(true);
    this.isLoading.set(true);

    if (this.form.invalid) return;

    this._requestService.searchRequestByDocument(this.form.value.document).subscribe({
      next: (resp: any)=>{
        if (resp.status === HttpStatusCode.Ok) {
          this.getRequest(resp.body.data.request_id, "with-document");
        } else if (resp.status === HttpStatusCode.Locked) {
          this.handleRequestLocked(resp);
        }else{
          this._customMessageService.responseNoticeHandler(resp);
        }
        this.isLoading.set(false);
      },
      error: (error: HttpErrorResponse)=> {
        // TODO mejorar manejo del error
        if(error.status == HttpStatusCode.NotAcceptable || error.status == HttpStatusCode.Locked) {
          this._customMessageService.logErrorOnly(error);
          this.isLoading.set(false);
          this.hasError.set(true);
          this.errorMessage = this.textErrorMessage;
        }else{
          this._customMessageService.responseNoticeHandler(error);
          this.isLoading.set(false);
        }
      }
    })

  }

  onViewError($event: any){
    if ($event.target.value === ''){
      this.hasError.set(false);
      this.errorMessage = '';
    }
  }

  updateRequest(data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this._requestService.update(this.requestID, data).subscribe({
        next: (resp) => {
          if (resp.status !== HttpStatusCode.NoContent) {
            this._customMessageService.responseNoticeHandler(resp);
          }else{
            resolve();
          }
        },
        error: (error: ResponseHttpError) => {
          if (error.status === HttpStatusCode.Locked) {
            this.handleRequestLocked(error as any);
          }else{
            this._customMessageService.responseNoticeHandler(error);
          }
          reject(error);
        }
      });
    });
  }

  getRequest(id: string, origin:string) {
    this.requestID = id;
    forkJoin({
      request: this._requestService.get(id),
      flows: this._salesWorkflowService.getList()
    }).subscribe({
      next: async (resp: any)=> {
        if(resp.request.status === HttpStatusCode.Ok && resp.flows.status === HttpStatusCode.Ok) {
          this._requestService.request = resp.request.body.data;
          this._requestService.categoryId = this._requestService.request['category_id'] ?? undefined;

          if(!await this.checkAndSetNextWorkflowStep(origin)){
            return
          }

          let data: { update_salesman_request_id?: boolean, category_step_id?: string, store?: { id: string, name: string } } = {};
          if(this._requestService.request['agreement'].status == AgreementStatus.FIRMADO) {
            data.category_step_id = this.currentCategoryStepId;
          }else{
            data = { update_salesman_request_id: true, category_step_id: this.currentCategoryStepId };
          }

          if(this.flowCode === SalesWorkflow['app-krdya-cliente'] || this.flowCode === SalesWorkflow['app-krdya-general-tienda']) {
            const userData = this._localStorageService.get(LocalStorageKey.data);
            if(userData?.store) {
              data.store = { id: userData.store.id, name: userData.store.name };
            }
          }

          await this.updateRequest(data);

          const url = this._salesWorkflowService.buildUrl({
            flowCode: this.flowCode,
            path: this.pathNext ?? '',
            categoryCode: this.categoryCode,
            requestId: id
          });
          this._router.navigate([url]);

        }else{
          this._customMessageService.responseNoticeHandler(resp);
        }
      },
      error: (error: ResponseHttpError) => {
        this.isLoading.set(false);
        this.hasError.set(true);
        this.errorMessage = this.textErrorMessage;
        this._customMessageService.responseNoticeHandler(error);
        if(this.visibleQRModal) this.startScanner();
        this._utilService.disablePageLoading();
      }
    })
  }

  async checkAndSetNextWorkflowStep(origin: string): Promise<boolean | null> {

    let nextStepsParameters = await this._salesWorkflowService.checkAndSetNextWorkflowStep();

    if(nextStepsParameters === true) return true;

    if(nextStepsParameters === "autogestion-cliente-incompleto") {
          this.isLoading.set(false);
          if (origin === "scanner" || origin === "with-document") {
            this.hasError.set(true);
          } else {
            this._customMessageService.responseNoticeHandler({message: `Valor de origin no valido: ${origin}`}, OriginMessageError.FRONTEND);
          }
          this.errorMessage = this.textErrorMessageInProgress;
          return false;
        }


    if (nextStepsParameters &&  typeof nextStepsParameters === 'object' && 'currentCategoryStepId' in nextStepsParameters && 'pathNext' in nextStepsParameters) {
      this.currentCategoryStepId = nextStepsParameters.currentCategoryStepId;
      this.pathNext = nextStepsParameters.pathNext;
      this.flowCode = nextStepsParameters.flowCode;
      this.categoryCode = nextStepsParameters.categoryCode;

      return true;
    } else {
      this.isLoading.set(false);
      this.hasError.set(true);
      this._customMessageService.responseNoticeHandler({message: `Valor de nextStepsParameters no valido: ${nextStepsParameters}`}, OriginMessageError.FRONTEND);
      this.errorMessage = this.textErrorMessage;
      return false;
    }

  }





  onContinue(){
    switch (this.selectedOption) {
      case this.option.codigoQR:
        this.visibleQRModal = true;
        this.startScanner();
        break;

      case this.option.documentoIdentidad:
        this.visibleDocumentModal = true;
        break;

      case this.option.listarSolicitudes:
        this._utilService.enablePageLoading();
        this.loadRequirements();
        break;
    }
  }

  private handleRequestLocked(resp: any): void {
    const messageError = resp.error?.message ?? 'Solicitud no disponible';
    const attemptedUrl = this.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotAvailable(this.flowCode, state);
  }

  getCurrentUrl(): string {
    return window.location.href;
  }

  loadRequirements() {
    this._requestService.getAll().subscribe({
      next: (resp: any)=> {
        if(resp.status === HttpStatusCode.Ok) {
          this.requests = resp.body.data.list;
          this.separateRequestsByType();
          this.stepRequest = true;
          this._utilService.disablePageLoading();
        }else{
          this._customMessageService.responseNoticeHandler(resp);
          this._utilService.disablePageLoading();
        }

      },
      error: (error: ResponseHttpError) => {
        this._customMessageService.responseNoticeHandler(error);
        this._utilService.disablePageLoading();
      }
    })
  }

  private separateRequestsByType(): void {
    this.homeRequests = this.requests.filter(request =>
      request.flow?.code === SalesWorkflow['domicilio-cliente'] ||
      request.flow?.code === SalesWorkflow['domicilio-vendedor']
    );
    this.storeRequests = this.requests.filter(request =>
      request.flow?.code !== SalesWorkflow['domicilio-cliente'] &&
      request.flow?.code !== SalesWorkflow['domicilio-vendedor']
    );

    this.storeRequestsCount = this.storeRequests.length;
    this.homeRequestsCount = this.homeRequests.length;
  }

  goToDetail(request: any) {
    if(!this.redirectRequirementOld(request)){
      return
    }
    this.getRequest(request.id, "detail");
  }

  onQRModalClose(){
    this.stopScanner();
  }

  redirectRequirementOld(request : any) {
    if(request.type_sales_flow == 'actual'){
      this._authService.gotoSalesModule(`solicitud/${request.id}/progreso`)
      return false
    }
    return true;
  }

  onSelectAndContinueRequest(request: any){
    if( ! this._macroFlowService.isExperanzaFlow(request.flow.code)) {
      return
    }

    request.status = 'CARGANDO...'
    this._requestService.getRequestAttributes(request.id, ATTRIBUTES_CODE).subscribe({
      next: (resp: any)=>{
        if (resp.status === HttpStatusCode.Ok) {
          request.status = resp.body.data.value.authorization.status;
        }else{
          request.status = 'En revisión'
        }
      },
      error: (error: HttpErrorResponse)=> {
        request.status = 'En revisión'
      }
    })
  }

  visibleSelectRequestButton(request: any) {
    return [RequestStatus.Aprobado.toLowerCase(), RequestStatus.INICIADO.toLowerCase()].includes(request.status.toLowerCase())
  }

  get f(){
    return this.form.controls;
  }

  goToHome(): void {
    this.stepRequest = false;
    this.selectedOption = null;
    this.requests = [];
    this.storeRequests = [];
    this.homeRequests = [];
    this.storeRequestsCount = 0;
    this.homeRequestsCount = 0;
    this._router.navigate(['/inicio']);
  }
}
